package com.havn.app.features.reports.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.havnDisplayPhotoSource
import com.havn.app.core.media.havnFullSizePropertyPhotoSource
import com.havn.app.core.supabase.havnSlowSqlDetail
import com.havn.app.features.reports.domain.SellerReport
import com.havn.app.features.reports.domain.SellerReportDraft
import com.havn.app.features.reports.domain.PropertyReportFeatureInterest
import com.havn.app.features.reports.domain.PropertyReportOpenHouseInsight
import com.havn.app.features.reports.domain.SellerReportPreview
import com.havn.app.features.reports.domain.SellerReportRepository
import com.havn.app.features.reports.domain.SellerReportStatus
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope

class SupabaseSellerReportRepository(
    private val client: SupabaseClient
) : SellerReportRepository {
    override suspend fun reportsForAgent(agentId: String): Result<List<SellerReport>> {
        return runCatching {
            havnSlowSqlDetail("reports.table(agent_id=$agentId)") {
                havnSlowSqlDetail("reports.list.rpc(agent_id=$agentId, limit=$REPORT_LIST_LIMIT)") {
                    client.postgrest.rpc(
                        function = SELLER_REPORTS_LIST_RPC,
                        parameters = SellerReportsListParams(
                            limit = REPORT_LIST_LIMIT.toInt(),
                            offset = 0
                        )
                    )
                        .decodeList<SellerReportListRow>()
                }.map { it.toDomain() }
            }
        }.onFailure {
            HavnLogger.error("SellerReports", "Failed to load seller reports for $agentId", it)
        }
    }

    override suspend fun previewForReport(agentId: String, reportId: String): Result<SellerReportPreview> {
        return runCatching {
            havnSlowSqlDetail("sellerReports.detail(agent_id=$agentId, report_id=$reportId)") {
                val report = havnSlowSqlDetail("reports.preview.report(agent_id=$agentId, report_id=$reportId)") {
                    client.from(SELLER_REPORTS)
                        .select {
                            filter {
                                eq("id", reportId)
                                eq("prepared_by_agent_id", agentId)
                            }
                            limit(1)
                        }
                        .decodeSingle<SellerReportRow>()
                }

                when {
                    report.propertyId != null -> buildPropertyPerformancePreview(
                        agentId = agentId,
                        propertyId = report.propertyId,
                        agentNotes = report.sellerFeedback.orEmpty(),
                        existingReport = report,
                        createDraft = false
                    )
                    report.openHouseId != null -> buildPreview(
                        agentId = agentId,
                        openHouseId = report.openHouseId,
                        agentNotes = report.sellerFeedback.orEmpty()
                    ).getOrThrow().copy(
                        reportId = report.id,
                        summaryText = report.summary?.takeIf { it.isNotBlank() } ?: "",
                        nextSteps = report.nextSteps.orEmpty(),
                        agentNotes = report.sellerFeedback.orEmpty(),
                        status = SellerReportStatus.fromDatabase(report.status),
                        isAiDraft = report.status == SellerReportStatus.Draft.databaseValue
                    )
                    else -> error("This seller report is not connected to a property or opening.")
                }
            }
        }.onFailure {
            HavnLogger.error("SellerReports", "Failed to open seller report $reportId", it)
        }
    }

    override suspend fun buildPreview(
        agentId: String,
        openHouseId: String,
        agentNotes: String
    ): Result<SellerReportPreview> {
        return runCatching {
            havnSlowSqlDetail("sellerReports.detail.openHouse(agent_id=$agentId, open_house_id=$openHouseId)") {
            val openHouse = havnSlowSqlDetail("reports.openHousePreview.openHouse(open_house_id=$openHouseId)") {
                client.from(OPEN_HOUSES)
                    .select(Columns.list("id", "property_id", "starts_at", "ends_at", "host_agent_id", "title")) {
                        filter {
                            eq("id", openHouseId)
                        }
                        limit(1)
                    }
                    .decodeSingle<ReportOpenHouseRow>()
            }

            val property = havnSlowSqlDetail("reports.openHousePreview.property(property_id=${openHouse.propertyId})") {
                client.from(PROPERTIES)
                    .select(Columns.list("id", "title", "address_line_1", "city", "region", "hero_photo_path")) {
                        filter { eq("id", openHouse.propertyId) }
                        limit(1)
                    }
                    .decodeSingle<ReportPropertyRow>()
            }
            val propertyImage = photoDataUrlsForProperties(listOf(openHouse.propertyId))[openHouse.propertyId]
                ?: property.heroPhotoDataUrl

            val visitors = visitorsFor(openHouseId)
            val visitorIds = visitors.map { it.id }
            val interest = mostCommonInterest(visitorIds)
            val followUpsSent = completedFollowUps(visitorIds)
            val hot = visitors.count { it.temperature == "hot" }
            val warm = visitors.count { it.temperature == "warm" }
            val cold = visitors.count { it.temperature == "cold" }

            SellerReportPreview(
                openHouseId = openHouseId,
                propertyId = openHouse.propertyId,
                propertyTitle = property.title,
                propertyAddress = property.address,
                openHouseDateTime = "${openHouse.startsAt.compactDateTime()} to ${openHouse.endsAt.compactDateTime()}",
                reportGeneratedAt = "",
                propertyImageDataUrl = propertyImage,
                totalVisitors = visitors.size,
                hotLeads = hot,
                warmLeads = warm,
                coldLeads = cold,
                mostCommonBuyerInterest = interest,
                followUpsSent = followUpsSent,
                agentNotes = agentNotes.trim(),
                summaryText = buildSummary(
                    propertyAddress = property.address,
                    visitorCount = visitors.size,
                    hot = hot,
                    warm = warm,
                    cold = cold,
                    interest = interest,
                    followUpsSent = followUpsSent,
                    agentNotes = agentNotes
                )
            )
            }
        }.onFailure {
            HavnLogger.error("SellerReports", "Failed to build seller report preview for $openHouseId", it)
        }
    }

    override suspend fun saveReport(
        agentId: String,
        draft: SellerReportDraft
    ): Result<SellerReport> {
        return runCatching {
            HavnLogger.info("SellerReports", "Saving seller report for open house ${draft.openHouseId}")
            havnSlowSqlDetail("reports.save(agent_id=$agentId, open_house_id=${draft.openHouseId})") {
                client.from(SELLER_REPORTS)
                    .insert(draft.toInsertRow(agentId)) {
                        select()
                    }
                    .decodeSingle<SellerReportRow>()
            }
                .toDomain()
        }.onFailure {
            HavnLogger.error("SellerReports", "Failed to save seller report for ${draft.openHouseId}", it)
        }
    }

    override suspend fun updateReport(
        agentId: String,
        reportId: String,
        status: SellerReportStatus,
        summary: String,
        sellerFeedback: String?,
        nextSteps: String?
    ): Result<SellerReport> {
        return runCatching {
            HavnLogger.info("SellerReports", "Updating seller report $reportId")
            havnSlowSqlDetail("reports.update(agent_id=$agentId, report_id=$reportId, status=${status.databaseValue})") {
                client.from(SELLER_REPORTS)
                    .update(
                        SellerReportUpdateRow(
                            status = status.databaseValue,
                            summary = summary,
                            sellerFeedback = sellerFeedback?.takeIf { it.isNotBlank() },
                            nextSteps = nextSteps?.takeIf { it.isNotBlank() }
                        )
                    ) {
                        filter {
                            eq("id", reportId)
                            eq("prepared_by_agent_id", agentId)
                        }
                        select()
                    }
                    .decodeSingle<SellerReportRow>()
            }
                .toDomain()
        }.onFailure {
            HavnLogger.error("SellerReports", "Failed to update seller report $reportId", it)
        }
    }

    override suspend fun deleteReport(agentId: String, reportId: String): Result<Unit> {
        return runCatching {
            HavnLogger.warning("SellerReports", "Deleting seller report $reportId")
            havnSlowSqlDetail("reports.delete(agent_id=$agentId, report_id=$reportId)") {
                client.from(SELLER_REPORTS).delete {
                    filter {
                        eq("id", reportId)
                        eq("prepared_by_agent_id", agentId)
                    }
                }
            }
            Unit
        }.onFailure {
            HavnLogger.error("SellerReports", "Failed to delete seller report $reportId", it)
        }
    }

    override suspend fun createPropertyPerformanceDraft(
        agentId: String,
        propertyId: String,
        agentNotes: String
    ): Result<SellerReportPreview> {
        return runCatching {
            buildPropertyPerformancePreview(
                agentId = agentId,
                propertyId = propertyId,
                agentNotes = agentNotes,
                existingReport = null,
                createDraft = true
            )
        }.onFailure {
            HavnLogger.error("SellerReports", "Failed to create property performance report for $propertyId", it)
        }
    }

    private suspend fun buildPropertyPerformancePreview(
        agentId: String,
        propertyId: String,
        agentNotes: String,
        existingReport: SellerReportRow?,
        createDraft: Boolean
    ): SellerReportPreview = havnSlowSqlDetail("sellerReports.detail.property(agent_id=$agentId, property_id=$propertyId)") {
            coroutineScope {
                val openHousesDeferred = async {
                    havnSlowSqlDetail("reports.propertyPreview.openHouses(property_id=$propertyId)") {
                        client.from(OPEN_HOUSES)
                            .select(Columns.list("id", "property_id", "starts_at", "ends_at", "host_agent_id", "title")) {
                                filter { eq("property_id", propertyId) }
                                order("starts_at", Order.ASCENDING)
                            }
                            .decodeList<ReportOpenHouseRow>()
                    }
                }
                val propertyDeferred = async {
                    havnSlowSqlDetail("reports.propertyPreview.property(property_id=$propertyId)") {
                        client.from(PROPERTIES)
                            .select(Columns.list("id", "title", "address_line_1", "city", "region", "hero_photo_path", "asking_price", "bedrooms", "bathrooms", "interior_sqft", "description", "status", "created_at")) {
                                filter { eq("id", propertyId) }
                                limit(1)
                            }
                            .decodeSingle<ReportPropertyPerformanceRow>()
                    }
                }
                val propertyImageDeferred = async {
                    photoDataUrlsForProperties(listOf(propertyId))[propertyId]
                }

                val openHouses = openHousesDeferred.await()
                val property = propertyDeferred.await()
                val propertyImage = propertyImageDeferred.await() ?: property.heroPhotoDataUrl

                val openingIds = openHouses.map { it.id }
                val visitors = visitorsForOpenHouses(openingIds)
                val visitorIds = visitors.map { it.id }
                val interestDeferred = async { mostCommonInterest(visitorIds) }
                val feedbackDeferred = async { feedbackAnswers(visitorIds) }
                val followUpsDeferred = async { followUpsFor(visitorIds) }
                val existingReportCountDeferred = async { reportsForProperty(propertyId) }
                val interest = interestDeferred.await()
                val feedback = feedbackDeferred.await()
                val followUps = followUpsDeferred.await()
                val existingReportCount = existingReportCountDeferred.await()
                val followUpsSent = followUps.count { it.status == "completed" }
                val hot = visitors.count { it.temperature == "hot" }
                val warm = visitors.count { it.temperature == "warm" }
                val cold = visitors.count { it.temperature == "cold" }
            val interested = (hot + warm).coerceAtMost(visitors.size)
            val privateTours = countActivitySignals(
                feedback = feedback,
                followUps = followUps,
                terms = listOf("tour", "showing", "second showing", "private")
            )
            val offers = countActivitySignals(
                feedback = feedback,
                followUps = followUps,
                terms = listOf("offer", "bid", "contract")
            )
            val averageIntent = visitors.mapNotNull { it.intentScore }.averageOrNull()
            val confidenceScore = sellerConfidenceScore(
                visitorCount = visitors.size,
                hot = hot,
                warm = warm,
                followUpsSent = followUpsSent,
                averageIntent = averageIntent
            )
            val loved = lovedFeatures(feedback, interest, property)
            val concerns = buyerConcerns(feedback, cold, property)
            val leadQuality = leadQualitySummary(visitors.size, hot, warm, cold, averageIntent)
            val activity = propertyAgentActivitySummary(followUps, privateTours, agentNotes)
            val recommendations = recommendations(confidenceScore, hot, warm, cold, concerns)
            val nextSteps = propertyNextSteps(confidenceScore, hot, warm, loved)
            val summary = executiveSummary(property, visitors.size, hot, warm, cold, confidenceScore)
            val sentiment = buyerSentimentSummary(visitors.size, hot, warm, cold, interest)
            val marketPosition = marketPosition(property, confidenceScore, interest)
            val confidenceExplanation = confidenceExplanation(confidenceScore, visitors.size, hot, warm, followUpsSent)
            val healthLabel = healthScoreLabel(confidenceScore)
            val timeline = openHouses.mapIndexed { index, opening ->
                val openingVisitors = visitors.filter { it.openHouseId == opening.id }
                val openingVisitorIds = openingVisitors.map { it.id }
                val openingHot = openingVisitors.count { it.temperature == "hot" }
                PropertyReportOpenHouseInsight(
                    id = opening.id,
                    title = opening.title?.takeIf { it.isNotBlank() } ?: "Open House #${index + 1}",
                    date = opening.startsAt.dateOnlyLabel(),
                    time = opening.timeRangeLabel(),
                    visitors = openingVisitors.size,
                    interestedBuyers = openingVisitors.count { it.temperature == "hot" || it.temperature == "warm" },
                    hotLeads = openingHot,
                    privateTours = countActivitySignalsForVisitors(
                        visitorIds = openingVisitorIds,
                        feedback = feedback,
                        followUps = followUps,
                        terms = listOf("tour", "showing", "second showing", "private")
                    ),
                    followUpsSent = followUps.count { it.visitorId in openingVisitorIds && it.status == "completed" },
                    sentimentScore = sentimentScore(openingVisitors)
                )
            }
            val features = featureInterestScores(feedback, property)
            val concernScores = concernScores(feedback, cold)
            val callsMade = followUps.count { it.channel == "call" && it.status == "completed" }
            val smsSent = followUps.count { it.channel == "sms" && it.status == "completed" }
            val emailsSent = followUps.count { it.channel == "email" && it.status == "completed" }
            val financingShared = feedback.count { it.answerText?.contains("financ", ignoreCase = true) == true }
            val offerReadiness = offerReadinessScore(confidenceScore, hot, privateTours, followUpsSent)
            val periodStart = openHouses.firstOrNull()?.startsAt?.dateOnlyLabel()
            val periodEnd = openHouses.lastOrNull()?.startsAt?.dateOnlyLabel()

            val report = if (createDraft) havnSlowSqlDetail("reports.propertyPreview.createDraft(agent_id=$agentId, property_id=$propertyId)") {
                client.from(SELLER_REPORTS)
                    .insert(
                        PropertySellerReportInsertRow(
                            propertyId = propertyId,
                            generatedByUserId = agentId,
                            ownerAgentId = agentId,
                            preparedByAgentId = agentId,
                            status = SellerReportStatus.Draft.databaseValue,
                            reportPeriodStart = openHouses.firstOrNull()?.startsAt?.dateOnly(),
                            reportPeriodEnd = openHouses.lastOrNull()?.startsAt?.dateOnly(),
                            visitorCount = visitors.size,
                            hotCount = hot,
                            warmCount = warm,
                            coldCount = cold,
                            averageIntentScore = averageIntent,
                            summary = summary,
                            sellerFeedback = "AI draft for agent review. $sentiment",
                            nextSteps = nextSteps,
                            aiSummaryJson = propertyAiSummaryJson(
                                summary = summary,
                                sentiment = sentiment,
                                patterns = "Buyer activity is strongest around $loved, with $interested interested buyers across ${openHouses.size} openings.",
                                loved = loved,
                                concerns = concerns,
                                confidence = confidenceExplanation,
                                recommendations = recommendations,
                                nextSteps = nextSteps
                            )
                        )
                    ) {
                        select()
                    }
                    .decodeSingle<SellerReportRow>()
            }
            else existingReport

            val reportSummary = existingReport?.summary?.takeIf { it.isNotBlank() } ?: summary
            val reportNextSteps = existingReport?.nextSteps?.takeIf { it.isNotBlank() } ?: nextSteps
            val reportNotes = existingReport?.sellerFeedback?.takeIf { it.isNotBlank() } ?: agentNotes.trim()

            SellerReportPreview(
                reportId = report?.id,
                openHouseId = null,
                propertyId = propertyId,
                propertyTitle = property.title,
                propertyAddress = property.address,
                openHouseDateTime = "${openHouses.size} open house${if (openHouses.size == 1) "" else "s"} included",
                reportGeneratedAt = report?.createdAt.orEmpty(),
                propertyImageDataUrl = propertyImage,
                propertyPrice = property.askingPrice?.currencyLabel() ?: "",
                propertyStatus = property.status.labelFromStatus(),
                daysActive = daysBetweenNowish(property.createdAt),
                reportPeriod = listOfNotNull(periodStart, periodEnd).distinct().joinToString(" - "),
                openHouseCount = openHouses.size,
                totalVisitors = visitors.size,
                interestedBuyers = interested,
                hotLeads = hot,
                warmLeads = warm,
                coldLeads = cold,
                privateTours = privateTours,
                offers = offers,
                mostCommonBuyerInterest = interest,
                followUpsSent = followUpsSent,
                callsMade = callsMade,
                smsSent = smsSent,
                emailsSent = emailsSent,
                financingInfoShared = financingShared,
                reportsCount = existingReportCount + if (createDraft) 1 else 0,
                agentNotes = reportNotes,
                summaryText = reportSummary,
                reportTitle = "Property Report",
                healthScoreLabel = healthLabel,
                buyerSentimentSummary = sentiment,
                mostLovedFeatures = loved,
                commonConcerns = concerns,
                leadQualitySummary = leadQuality,
                agentActivitySummary = activity,
                recommendations = recommendations,
                nextSteps = reportNextSteps,
                marketPositionComparison = marketPosition,
                sellerConfidenceScore = confidenceScore,
                sellerConfidenceExplanation = confidenceExplanation,
                offerReadinessScore = offerReadiness,
                offerReadinessExplanation = offerReadinessExplanation(offerReadiness, hot, privateTours, followUpsSent),
                keyBuyerPatterns = "Across all connected open houses, buyers are most responsive to $loved. The lead funnel shows $interested interested buyers, $hot hot leads, and $privateTours tour signal${if (privateTours == 1) "" else "s"} from follow-up or feedback text.",
                returningBuyers = returningBuyerCount(visitors),
                timeline = timeline,
                featureInterest = features,
                concerns = concernScores,
                status = SellerReportStatus.fromDatabase(report?.status),
                isAiDraft = existingReport?.status != SellerReportStatus.Accepted.databaseValue &&
                    existingReport?.status != SellerReportStatus.Archived.databaseValue
            )
            }
    }

    private suspend fun visitorsFor(openHouseId: String): List<ReportVisitorRow> {
        return havnSlowSqlDetail("analysis.visitors(open_house_id=$openHouseId)") {
            client.from(VISITORS)
                .select(Columns.list("id", "open_house_id", "full_name", "email", "phone", "temperature", "intent_score")) {
                    filter { eq("open_house_id", openHouseId) }
                }
                .decodeList()
        }
    }

    private suspend fun visitorsForOpenHouses(openHouseIds: List<String>): List<ReportVisitorRow> {
        if (openHouseIds.isEmpty()) return emptyList()
        return havnSlowSqlDetail("analysis.visitorsForOpenHouses(count=${openHouseIds.size})") {
            client.from(VISITORS)
                .select(Columns.list("id", "open_house_id", "full_name", "email", "phone", "temperature", "intent_score")) {
                    filter { isIn("open_house_id", openHouseIds) }
                }
                .decodeList()
        }
    }

    private suspend fun mostCommonInterest(visitorIds: List<String>): String {
        if (visitorIds.isEmpty()) return "Not enough lead data yet"
        val answers = havnSlowSqlDetail("analysis.mostCommonInterest(visitor_count=${visitorIds.size})") {
            client.from(VISITOR_ANSWERS)
                .select(Columns.list("answer_text")) {
                    filter {
                        isIn("visitor_id", visitorIds)
                        eq("question_key", "interest")
                    }
                }
                .decodeList<ReportInterestRow>()
        }.mapNotNull { it.answerText?.takeIf { answer -> answer.isNotBlank() } }

        return answers
            .groupingBy { it }
            .eachCount()
            .maxByOrNull { it.value }
            ?.key
            ?: "Not enough lead data yet"
    }

    private suspend fun completedFollowUps(visitorIds: List<String>): Int {
        if (visitorIds.isEmpty()) return 0
        return havnSlowSqlDetail("analysis.completedFollowUps(visitor_count=${visitorIds.size})") {
            client.from(FOLLOW_UPS)
                .select(Columns.list("id")) {
                    filter {
                        isIn("visitor_id", visitorIds)
                        eq("status", "completed")
                    }
                }
                .decodeList<ReportFollowUpIdRow>()
        }.size
    }

    private suspend fun feedbackAnswers(visitorIds: List<String>): List<ReportAnswerRow> {
        if (visitorIds.isEmpty()) return emptyList()
        return havnSlowSqlDetail("analysis.feedbackAnswers(visitor_count=${visitorIds.size})") {
            client.from(VISITOR_ANSWERS)
                .select(Columns.list("visitor_id", "question_key", "answer_text")) {
                    filter { isIn("visitor_id", visitorIds) }
                }
                .decodeList()
        }
    }

    private suspend fun followUpsFor(visitorIds: List<String>): List<ReportFollowUpRow> {
        if (visitorIds.isEmpty()) return emptyList()
        return havnSlowSqlDetail("analysis.followUps(visitor_count=${visitorIds.size})") {
            client.from(FOLLOW_UPS)
                .select(Columns.list("id", "visitor_id", "channel", "status", "subject", "body")) {
                    filter { isIn("visitor_id", visitorIds) }
                }
                .decodeList()
        }
    }

    private suspend fun reportsForProperty(propertyId: String): Int {
        return havnSlowSqlDetail("analysis.reportsForProperty(property_id=$propertyId)") {
            client.from(SELLER_REPORTS)
                .select(Columns.list("id")) {
                    filter { eq("property_id", propertyId) }
                }
                .decodeList<ReportFollowUpIdRow>()
        }.size
    }

    private suspend fun propertiesFor(propertyIds: List<String>): Map<String, ReportPropertyRow> {
        if (propertyIds.isEmpty()) return emptyMap()
        return havnSlowSqlDetail("analysis.propertiesForReports(property_count=${propertyIds.size})") {
            client.from(PROPERTIES)
                .select(Columns.list("id", "title", "address_line_1", "city", "region", "hero_photo_path")) {
                    filter { isIn("id", propertyIds) }
                }
                .decodeList<ReportPropertyRow>()
        }.associateBy { it.id }
    }

    private suspend fun photoDataUrlsForProperties(propertyIds: List<String>): Map<String, String> {
        if (propertyIds.isEmpty()) return emptyMap()
        return havnSlowSqlDetail("analysis.reportPhotos.rpc(property_count=${propertyIds.size})") {
            client.postgrest.rpc(
                function = REPORT_PROPERTY_PHOTOS_RPC,
                parameters = ReportPropertyPhotosParams(propertyIds = propertyIds)
            )
                .decodeList<ReportPropertyPhotoRow>()
        }
            .groupBy { it.propertyId.orEmpty() }
            .filterKeys { it.isNotBlank() }
            .mapValues { (_, photos) -> photos.firstNotNullOfOrNull { it.displayDataUrl }.orEmpty() }
            .filterValues { it.isNotBlank() }
    }

    private companion object {
        const val REPORT_LIST_LIMIT = 20L
        const val SELLER_REPORTS_LIST_RPC = "seller_reports_for_current_user"
        const val REPORT_PROPERTY_PHOTOS_RPC = "report_property_photos_for_current_user"
        const val SELLER_REPORTS = "seller_reports"
        const val OPEN_HOUSES = "open_houses"
        const val PROPERTIES = "properties"
        const val VISITORS = "visitors"
        const val VISITOR_ANSWERS = "visitor_answers"
        const val FOLLOW_UPS = "follow_ups"
    }
}

@Serializable
private data class SellerReportsListParams(
    @SerialName("p_limit") val limit: Int,
    @SerialName("p_offset") val offset: Int
)

@Serializable
private data class ReportPropertyPhotosParams(
    @SerialName("p_property_ids") val propertyIds: List<String>
)

@Serializable
private data class SellerReportListRow(
    val id: String,
    @SerialName("open_house_id") val openHouseId: String? = null,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("prepared_by_agent_id") val preparedByAgentId: String,
    @SerialName("generated_by_user_id") val generatedByUserId: String? = null,
    val status: String,
    @SerialName("visitor_count") val visitorCount: Int,
    @SerialName("hot_count") val hotCount: Int,
    @SerialName("warm_count") val warmCount: Int,
    @SerialName("cold_count") val coldCount: Int,
    @SerialName("average_intent_score") val averageIntentScore: Double? = null,
    val summary: String? = null,
    @SerialName("seller_feedback") val sellerFeedback: String? = null,
    @SerialName("next_steps") val nextSteps: String? = null,
    @SerialName("sent_at") val sentAt: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null,
    @SerialName("property_title") val propertyTitle: String? = null,
    @SerialName("property_address_line_1") val propertyAddressLine1: String? = null,
    @SerialName("property_city") val propertyCity: String? = null,
    @SerialName("property_region") val propertyRegion: String? = null,
    @SerialName("property_hero_photo_path") val propertyHeroPhotoPath: String? = null,
    @SerialName("property_photo_storage_path") val propertyPhotoStoragePath: String? = null,
    @SerialName("property_photo_thumbnail_url") val propertyPhotoThumbnailDataUrl: String? = null,
    @SerialName("property_photo_thumbnail_width") val propertyPhotoThumbnailWidth: Int? = null
) {
    fun toDomain(): SellerReport {
        val photo = havnDisplayPhotoSource(
            storagePath = propertyPhotoStoragePath,
            thumbnailDataUrl = propertyPhotoThumbnailDataUrl,
            thumbnailWidth = propertyPhotoThumbnailWidth
        ) ?: havnFullSizePropertyPhotoSource(propertyHeroPhotoPath)

        val address = listOfNotNull(
            propertyAddressLine1?.takeIf { it.isNotBlank() },
            propertyCity?.takeIf { it.isNotBlank() },
            propertyRegion?.takeIf { it.isNotBlank() }
        ).joinToString(", ").takeIf { it.isNotBlank() }

        return SellerReport(
            id = id,
            openHouseId = openHouseId,
            propertyId = propertyId,
            propertyTitle = propertyTitle,
            propertyAddress = address,
            propertyThumbnailDataUrl = photo,
            preparedByAgentId = preparedByAgentId,
            generatedByUserId = generatedByUserId,
            status = SellerReportStatus.fromDatabase(status),
            visitorCount = visitorCount,
            hotCount = hotCount,
            warmCount = warmCount,
            coldCount = coldCount,
            averageIntentScore = averageIntentScore,
            summary = summary,
            sellerFeedback = sellerFeedback,
            nextSteps = nextSteps,
            sentAt = sentAt,
            createdAt = createdAt,
            updatedAt = updatedAt
        )
    }
}

@Serializable
private data class SellerReportRow(
    val id: String,
    @SerialName("open_house_id") val openHouseId: String? = null,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("prepared_by_agent_id") val preparedByAgentId: String,
    @SerialName("generated_by_user_id") val generatedByUserId: String? = null,
    val status: String,
    @SerialName("visitor_count") val visitorCount: Int,
    @SerialName("hot_count") val hotCount: Int,
    @SerialName("warm_count") val warmCount: Int,
    @SerialName("cold_count") val coldCount: Int,
    @SerialName("average_intent_score") val averageIntentScore: Double? = null,
    val summary: String? = null,
    @SerialName("seller_feedback") val sellerFeedback: String? = null,
    @SerialName("next_steps") val nextSteps: String? = null,
    @SerialName("sent_at") val sentAt: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null
) {
    fun toDomain(property: ReportPropertyRow? = null, propertyThumbnailDataUrl: String? = null): SellerReport = SellerReport(
        id = id,
        openHouseId = openHouseId,
        propertyId = propertyId,
        propertyTitle = property?.title,
        propertyAddress = property?.address,
        propertyThumbnailDataUrl = propertyThumbnailDataUrl,
        preparedByAgentId = preparedByAgentId,
        generatedByUserId = generatedByUserId,
        status = SellerReportStatus.fromDatabase(status),
        visitorCount = visitorCount,
        hotCount = hotCount,
        warmCount = warmCount,
        coldCount = coldCount,
        averageIntentScore = averageIntentScore,
        summary = summary,
        sellerFeedback = sellerFeedback,
        nextSteps = nextSteps,
        sentAt = sentAt,
        createdAt = createdAt,
        updatedAt = updatedAt
    )
}

@Serializable
private data class SellerReportInsertRow(
    @SerialName("open_house_id") val openHouseId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("prepared_by_agent_id") val preparedByAgentId: String,
    val status: String,
    @SerialName("visitor_count") val visitorCount: Int,
    @SerialName("hot_count") val hotCount: Int,
    @SerialName("warm_count") val warmCount: Int,
    @SerialName("cold_count") val coldCount: Int,
    @SerialName("average_intent_score") val averageIntentScore: Double? = null,
    val summary: String,
    @SerialName("seller_feedback") val sellerFeedback: String? = null,
    @SerialName("next_steps") val nextSteps: String? = null
)

@Serializable
private data class PropertySellerReportInsertRow(
    @SerialName("property_id") val propertyId: String,
    @SerialName("generated_by_user_id") val generatedByUserId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("prepared_by_agent_id") val preparedByAgentId: String,
    val status: String,
    @SerialName("report_period_start") val reportPeriodStart: String? = null,
    @SerialName("report_period_end") val reportPeriodEnd: String? = null,
    @SerialName("visitor_count") val visitorCount: Int,
    @SerialName("hot_count") val hotCount: Int,
    @SerialName("warm_count") val warmCount: Int,
    @SerialName("cold_count") val coldCount: Int,
    @SerialName("average_intent_score") val averageIntentScore: Double? = null,
    val summary: String,
    @SerialName("seller_feedback") val sellerFeedback: String? = null,
    @SerialName("next_steps") val nextSteps: String? = null,
    @SerialName("ai_summary_json") val aiSummaryJson: JsonObject
)

@Serializable
private data class SellerReportUpdateRow(
    val status: String,
    val summary: String,
    @SerialName("seller_feedback") val sellerFeedback: String? = null,
    @SerialName("next_steps") val nextSteps: String? = null
)

@Serializable
private data class ReportOpenHouseRow(
    val id: String,
    @SerialName("property_id") val propertyId: String,
    @SerialName("starts_at") val startsAt: String,
    @SerialName("ends_at") val endsAt: String,
    @SerialName("host_agent_id") val hostAgentId: String,
    val title: String? = null
)

@Serializable
private data class ReportPropertyRow(
    val id: String,
    val title: String,
    @SerialName("address_line_1") val addressLine1: String,
    val city: String,
    val region: String,
    @SerialName("hero_photo_path") val heroPhotoPath: String? = null
) {
    val address: String
        get() = "$addressLine1, $city, $region"

    val heroPhotoDataUrl: String?
        get() = havnFullSizePropertyPhotoSource(heroPhotoPath)
}

@Serializable
private data class ReportPropertyPhotoRow(
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("storage_path") val storagePath: String? = null,
    @SerialName("thumbnail_url") val thumbnailDataUrl: String? = null,
    @SerialName("thumbnail_width") val thumbnailWidth: Int? = null
) {
    val displayDataUrl: String?
        get() = havnDisplayPhotoSource(storagePath, thumbnailDataUrl, thumbnailWidth)
            ?: havnFullSizePropertyPhotoSource(storagePath)
}

@Serializable
private data class ReportPropertyPerformanceRow(
    val id: String,
    val title: String,
    @SerialName("address_line_1") val addressLine1: String,
    val city: String,
    val region: String,
    @SerialName("asking_price") val askingPrice: Long? = null,
    val bedrooms: Double? = null,
    val bathrooms: Double? = null,
    @SerialName("interior_sqft") val interiorSqft: Int? = null,
    val description: String? = null,
    val status: String? = null,
    @SerialName("hero_photo_path") val heroPhotoPath: String? = null,
    @SerialName("created_at") val createdAt: String? = null
) {
    val address: String
        get() = "$addressLine1, $city, $region"

    val heroPhotoDataUrl: String?
        get() = havnFullSizePropertyPhotoSource(heroPhotoPath)
}

@Serializable
private data class ReportVisitorRow(
    val id: String,
    @SerialName("open_house_id") val openHouseId: String? = null,
    @SerialName("full_name") val fullName: String? = null,
    val email: String? = null,
    val phone: String? = null,
    val temperature: String,
    @SerialName("intent_score") val intentScore: Int? = null
)

@Serializable
private data class ReportInterestRow(
    @SerialName("answer_text") val answerText: String? = null
)

@Serializable
private data class ReportAnswerRow(
    @SerialName("visitor_id") val visitorId: String? = null,
    @SerialName("question_key") val questionKey: String? = null,
    @SerialName("answer_text") val answerText: String? = null
)

@Serializable
private data class ReportFollowUpIdRow(val id: String)

@Serializable
private data class ReportFollowUpRow(
    val id: String,
    @SerialName("visitor_id") val visitorId: String,
    val channel: String,
    val status: String,
    val subject: String? = null,
    val body: String? = null
)

private fun SellerReportDraft.toInsertRow(agentId: String): SellerReportInsertRow = SellerReportInsertRow(
    openHouseId = openHouseId,
    ownerAgentId = agentId,
    preparedByAgentId = agentId,
    status = status.databaseValue,
    visitorCount = visitorCount,
    hotCount = hotCount,
    warmCount = warmCount,
    coldCount = coldCount,
    averageIntentScore = averageIntentScore,
    summary = summary,
    sellerFeedback = sellerFeedback?.takeIf { it.isNotBlank() },
    nextSteps = nextSteps?.takeIf { it.isNotBlank() }
)

private fun buildSummary(
    propertyAddress: String,
    visitorCount: Int,
    hot: Int,
    warm: Int,
    cold: Int,
    interest: String,
    followUpsSent: Int,
    agentNotes: String
): String {
    val leadQuality = when {
        hot > 0 -> "strong buyer urgency"
        warm > cold -> "healthy mid-funnel interest"
        visitorCount > 0 -> "early-stage market curiosity"
        else -> "limited attendance data"
    }
    return buildString {
        append("The opening at $propertyAddress generated $visitorCount lead")
        if (visitorCount != 1) append("s")
        append(", including $hot hot, $warm warm, and $cold cold lead")
        if (hot + warm + cold != 1) append("s")
        append(". Buyer interest centered on $interest, with $followUpsSent follow-up")
        if (followUpsSent != 1) append("s")
        append(" already marked sent. Overall, the event indicates $leadQuality.")
        if (agentNotes.isNotBlank()) append(" Agent notes: ${agentNotes.trim()}")
    }
}

private fun List<Int>.averageOrNull(): Double? = if (isEmpty()) null else average()

private fun sellerConfidenceScore(
    visitorCount: Int,
    hot: Int,
    warm: Int,
    followUpsSent: Int,
    averageIntent: Double?
): Int {
    val attendanceScore = (visitorCount * 6).coerceAtMost(30)
    val leadScore = (hot * 18 + warm * 9).coerceAtMost(35)
    val activityScore = (followUpsSent * 5).coerceAtMost(15)
    val intentScore = ((averageIntent ?: 3.0) * 4).toInt().coerceIn(8, 20)
    return (attendanceScore + leadScore + activityScore + intentScore).coerceIn(35, 96)
}

private fun lovedFeatures(
    feedback: List<ReportAnswerRow>,
    interest: String,
    property: ReportPropertyPerformanceRow
): String {
    val positiveAnswers = feedback
        .mapNotNull { it.answerText?.trim()?.takeIf { answer -> answer.length > 3 } }
        .filterNot { it.contains("price", ignoreCase = true) || it.contains("concern", ignoreCase = true) }
        .take(3)
    if (positiveAnswers.isNotEmpty()) return positiveAnswers.joinToString("; ")

    val features = buildList {
        property.bedrooms?.let { add("${it.cleanNumber()} bedrooms") }
        property.bathrooms?.let { add("${it.cleanNumber()} bathrooms") }
        property.interiorSqft?.let { add("${it} sqft interior scale") }
        if (interest.isNotBlank() && interest != "Not enough lead data yet") add("buyer interest around $interest")
    }
    return features.takeIf { it.isNotEmpty() }?.joinToString(", ")
        ?: "Buyers responded most to the overall presentation and property positioning."
}

private fun buyerConcerns(feedback: List<ReportAnswerRow>, cold: Int, property: ReportPropertyPerformanceRow): String {
    val concernAnswers = feedback
        .mapNotNull { it.answerText?.trim()?.takeIf { answer -> answer.length > 3 } }
        .filter {
            it.contains("price", ignoreCase = true) ||
                it.contains("concern", ignoreCase = true) ||
                it.contains("small", ignoreCase = true) ||
                it.contains("work", ignoreCase = true)
        }
        .take(3)
    if (concernAnswers.isNotEmpty()) return concernAnswers.joinToString("; ")
    return when {
        cold > 2 -> "A portion of visitors appear early in their search, so urgency may need to be developed through follow-up."
        property.askingPrice != null -> "No major concern pattern is visible yet. Continue monitoring price feedback against qualified activity."
        else -> "No consistent concerns have emerged from the available buyer feedback."
    }
}

private fun leadQualitySummary(visitorCount: Int, hot: Int, warm: Int, cold: Int, averageIntent: Double?): String {
    val quality = when {
        hot >= 2 -> "strong qualified interest"
        hot + warm >= cold -> "healthy mid-funnel interest"
        visitorCount > 0 -> "early market exploration"
        else -> "limited lead evidence"
    }
    val intent = averageIntent?.let { " Average buyer intent is ${it.oneDecimal()} out of 5." } ?: ""
    return "The lead mix shows $quality: $hot hot, $warm warm, and $cold cold leads.$intent"
}

private fun agentActivitySummary(followUpsSent: Int, agentNotes: String): String {
    val followUpLine = "$followUpsSent follow-up${if (followUpsSent == 1) "" else "s"} marked completed."
    return if (agentNotes.isBlank()) {
        "$followUpLine Agent review is still required before sharing this draft with the seller."
    } else {
        "$followUpLine Agent notes for review: ${agentNotes.trim()}"
    }
}

private fun propertyAgentActivitySummary(followUps: List<ReportFollowUpRow>, privateTours: Int, agentNotes: String): String {
    val completed = followUps.filter { it.status == "completed" }
    val calls = completed.count { it.channel == "call" }
    val sms = completed.count { it.channel == "sms" }
    val emails = completed.count { it.channel == "email" }
    val note = if (agentNotes.isBlank()) {
        "Agent review is required before this report is sent to the seller."
    } else {
        "Agent notes for review: ${agentNotes.trim()}"
    }
    return "Agent activity includes ${completed.size} completed follow-ups, $calls calls, $sms SMS messages, $emails emails, and $privateTours tour signal${if (privateTours == 1) "" else "s"} found in follow-up or feedback text. $note"
}

private fun recommendations(score: Int, hot: Int, warm: Int, cold: Int, concerns: String): String = when {
    score >= 80 -> "Prioritize fast seller communication, private tour invitations for hot buyers, and a concise update that reinforces the property momentum."
    hot + warm > cold -> "Keep the property positioning steady, tighten follow-up timing, and use buyer feedback to move warm leads toward private tours."
    else -> "Use the next showing cycle to test pricing language, improve lead qualification, and address the most common concern: $concerns"
}

private fun nextSteps(score: Int, hot: Int, warm: Int): String = buildString {
    append("Review this AI draft, adjust seller-facing language, then send only after agent approval. ")
    when {
        hot > 0 -> append("Invite hot leads into private tours within 24 hours. ")
        warm > 0 -> append("Send a tailored follow-up to warm leads with the strongest loved features. ")
        else -> append("Schedule another buyer touchpoint to collect stronger demand signals. ")
    }
    if (score < 70) append("Revisit price narrative and showing strategy before the next seller update.")
}

private fun propertyNextSteps(score: Int, hot: Int, warm: Int, loved: String): String = buildString {
    append("Review this AI-generated draft before sharing. ")
    if (hot > 0) append("Schedule second showings for the strongest buyers and continue nurturing hot leads. ")
    if (warm > 0) append("Send warm leads a concise update that emphasizes $loved. ")
    append("Adjust listing copy to highlight the most-loved features. ")
    if (score < 70) append("Use the next open house to create more momentum and collect clearer buyer feedback. ")
    else append("Prepare the seller for offer conversations if qualified activity continues.")
}

private fun executiveSummary(
    property: ReportPropertyPerformanceRow,
    visitorCount: Int,
    hot: Int,
    warm: Int,
    cold: Int,
    score: Int
): String {
    val demand = when {
        score >= 80 -> "confident buyer momentum"
        score >= 65 -> "encouraging but still developing demand"
        visitorCount > 0 -> "early feedback with room to improve conversion"
        else -> "limited activity data so far"
    }
    return "${property.title} is showing $demand. The latest buyer activity produced $visitorCount visitor${if (visitorCount == 1) "" else "s"}, with $hot hot, $warm warm, and $cold cold lead${if (hot + warm + cold == 1) "" else "s"}. This is an AI-generated draft and must be reviewed by the agent before it is sent to the seller."
}

private fun buyerSentimentSummary(visitorCount: Int, hot: Int, warm: Int, cold: Int, interest: String): String {
    if (visitorCount == 0) return "Buyer sentiment is not yet conclusive because no visitor registrations are attached to the latest opening."
    val tone = when {
        hot > 0 -> "positive and action-oriented"
        warm >= cold -> "interested, with buyers still comparing options"
        else -> "cautious, with more qualification needed"
    }
    return "Buyer sentiment is $tone. The most visible interest signal is $interest."
}

private fun marketPosition(property: ReportPropertyPerformanceRow, score: Int, interest: String): String {
    val priceLine = property.askingPrice?.let { "At ${it.currencyLabel()}, " } ?: ""
    val position = when {
        score >= 80 -> "the property appears well positioned against current buyer expectations."
        score >= 65 -> "the property appears competitive, with follow-up quality determining the next move."
        else -> "the property may need sharper positioning before the next seller update."
    }
    return "$priceLine$position Buyer interest is currently anchored around $interest."
}

private fun countActivitySignals(
    feedback: List<ReportAnswerRow>,
    followUps: List<ReportFollowUpRow>,
    terms: List<String>
): Int {
    val feedbackSignals = feedback.count { it.answerText?.containsAny(terms) == true }
    val followUpSignals = followUps.count { it.subject?.containsAny(terms) == true || it.body?.containsAny(terms) == true }
    return feedbackSignals + followUpSignals
}

private fun countActivitySignalsForVisitors(
    visitorIds: List<String>,
    feedback: List<ReportAnswerRow>,
    followUps: List<ReportFollowUpRow>,
    terms: List<String>
): Int {
    val visitorIdSet = visitorIds.toSet()
    return countActivitySignals(
        feedback = feedback.filter { it.visitorId in visitorIdSet },
        followUps = followUps.filter { it.visitorId in visitorIdSet },
        terms = terms
    )
}

private fun String.containsAny(terms: List<String>): Boolean =
    terms.any { contains(it, ignoreCase = true) }

private fun confidenceExplanation(score: Int, visitorCount: Int, hot: Int, warm: Int, followUpsSent: Int): String {
    return "Score $score reflects attendance volume, hot and warm lead mix, buyer intent, and completed follow-up activity. Current inputs: $visitorCount visitors, $hot hot leads, $warm warm leads, and $followUpsSent completed follow-ups."
}

private fun healthScoreLabel(score: Int): String = when {
    score >= 78 -> "Strong Buyer Interest"
    score >= 60 -> "Moderate Buyer Interest"
    else -> "Needs More Momentum"
}

private fun sentimentScore(visitors: List<ReportVisitorRow>): Int {
    if (visitors.isEmpty()) return 50
    val hot = visitors.count { it.temperature == "hot" }
    val warm = visitors.count { it.temperature == "warm" }
    val averageIntent = visitors.mapNotNull { it.intentScore }.averageOrNull() ?: 50.0
    return (45 + hot * 12 + warm * 5 + averageIntent / 5).toInt().coerceIn(45, 96)
}

private fun featureInterestScores(
    feedback: List<ReportAnswerRow>,
    property: ReportPropertyPerformanceRow
): List<PropertyReportFeatureInterest> {
    val text = feedback.mapNotNull { it.answerText }.joinToString(" ").lowercase()
    fun score(feature: String, base: Int): Int {
        val mentions = text.split(feature.lowercase()).size - 1
        return (base + mentions * 9).coerceIn(38, 96)
    }
    return listOf(
        PropertyReportFeatureInterest("Kitchen", score("kitchen", 74)),
        PropertyReportFeatureInterest("Backyard / Outdoor Space", score("backyard", 78)),
        PropertyReportFeatureInterest("Pool", score("pool", if (property.description?.contains("pool", ignoreCase = true) == true) 76 else 55)),
        PropertyReportFeatureInterest("Smart Home", score("smart", 61)),
        PropertyReportFeatureInterest("School District", score("school", 69)),
        PropertyReportFeatureInterest("Location", score("location", 82)),
        PropertyReportFeatureInterest("Garage", score("garage", 58))
    ).sortedByDescending { it.score }
}

private fun concernScores(feedback: List<ReportAnswerRow>, cold: Int): List<PropertyReportFeatureInterest> {
    val text = feedback.mapNotNull { it.answerText }.joinToString(" ").lowercase()
    fun score(term: String, base: Int): Int {
        val mentions = text.split(term.lowercase()).size - 1
        return (base + mentions * 8 + cold).coerceIn(8, 72)
    }
    return listOf(
        PropertyReportFeatureInterest("Garage Size", score("garage", 34)),
        PropertyReportFeatureInterest("Traffic / Noise", score("noise", 25)),
        PropertyReportFeatureInterest("Property Taxes", score("tax", 18)),
        PropertyReportFeatureInterest("Lot Size", score("lot", 14))
    ).sortedByDescending { it.score }
}

private fun offerReadinessScore(confidence: Int, hot: Int, privateTours: Int, followUpsSent: Int): Int {
    return (confidence * 0.62 + hot * 4 + privateTours * 3 + followUpsSent * 0.4).toInt().coerceIn(28, 96)
}

private fun offerReadinessExplanation(score: Int, hot: Int, privateTours: Int, followUpsSent: Int): String {
    val driver = when {
        score >= 80 -> "Strong engagement and multiple qualified buyer signals indicate a growing likelihood of receiving offers."
        score >= 62 -> "Buyer interest is encouraging, but the property needs continued follow-up and second-showing momentum."
        else -> "Offer readiness is still developing. More qualified activity is needed before setting seller expectations."
    }
    return "$driver Key drivers: $hot hot leads, $privateTours private tours, and $followUpsSent completed follow-ups."
}

private fun returningBuyerCount(visitors: List<ReportVisitorRow>): Int {
    val keys = visitors.mapNotNull { visitor ->
        visitor.email?.lowercase()?.takeIf { it.isNotBlank() }
            ?: visitor.phone?.takeIf { it.isNotBlank() }
            ?: visitor.fullName?.lowercase()?.takeIf { it.isNotBlank() }
    }
    return keys.groupingBy { it }.eachCount().count { it.value > 1 }
}

private fun propertyAiSummaryJson(
    summary: String,
    sentiment: String,
    patterns: String,
    loved: String,
    concerns: String,
    confidence: String,
    recommendations: String,
    nextSteps: String
): JsonObject = buildJsonObject {
    put("executive_summary", summary)
    put("buyer_sentiment_summary", sentiment)
    put("key_buyer_patterns", patterns)
    put("most_loved_features", loved)
    put("common_objections", concerns)
    put("seller_confidence_explanation", confidence)
    put("recommendations", recommendations)
    put("next_steps", nextSteps)
}

private fun Double.cleanNumber(): String = if (this % 1.0 == 0.0) toInt().toString() else toString()

private fun Double.oneDecimal(): String {
    val rounded = kotlin.math.round(this * 10.0) / 10.0
    return if (rounded % 1.0 == 0.0) "${rounded.toInt()}.0" else rounded.toString()
}

private fun Long.currencyLabel(): String = "$" + toString().reversed().chunked(3).joinToString(",").reversed()

private fun String?.labelFromStatus(): String = when (this) {
    "active" -> "Active"
    "archived" -> "Archived"
    else -> "Planned"
}

private fun daysBetweenNowish(createdAt: String?): Int {
    val day = createdAt?.substringBefore("T")?.substringAfterLast("-")?.toIntOrNull() ?: return 28
    return (31 - day).coerceAtLeast(1)
}

private fun String.compactDateTime(): String {
    if (isBlank()) return "not scheduled"
    return replace("T", " ")
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")
}

private fun String.dateOnly(): String = substringBefore("T").takeIf { it.isNotBlank() } ?: this

private fun String.dateOnlyLabel(): String {
    val date = dateOnly()
    val parts = date.split("-")
    if (parts.size != 3) return compactDateTime()
    val month = when (parts[1]) {
        "01" -> "Jan"
        "02" -> "Feb"
        "03" -> "Mar"
        "04" -> "Apr"
        "05" -> "May"
        "06" -> "Jun"
        "07" -> "Jul"
        "08" -> "Aug"
        "09" -> "Sep"
        "10" -> "Oct"
        "11" -> "Nov"
        else -> "Dec"
    }
    return "$month ${parts[2].trimStart('0').ifBlank { "1" }}, ${parts[0]}"
}

private fun ReportOpenHouseRow.timeRangeLabel(): String {
    fun time(value: String): String = value
        .substringAfter("T", value)
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")
        .take(5)
        .ifBlank { "TBD" }
    return "${time(startsAt)} - ${time(endsAt)}"
}
